import React, { useMemo, useRef, useState } from 'react'
import { useNodeConnections, useNodesData, useReactFlow, type NodeProps } from '@xyflow/react'
import type { StudioWebPage } from '@genieartinc/schemas/graph'
import { FrameConnection } from '@/components/canvas/nodes/variants/FrameConnection'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useStudio } from '@/components/studio/StudioContext'
import { workflowImages } from '@/components/studio/outputs'

/**
 * Frame Output — an image-first node showing the result it will append to an
 * Artboard frame. Which result to use stays here because it depends on the live
 * incoming connection; the destination frame and parent version are
 * configuration and live in the node settings panel.
 */
function FrameOutputComponent({ id, data, selected }: NodeProps) {
  const studio = useStudio(); const flow = useReactFlow(); const update = useNodeStateChange(id)
  const state = (data.state ?? {}) as Record<string, unknown>
  const connections = useNodeConnections({ handleType: 'target' })
  const sourceIds = useMemo(() => connections.map(c => c.source), [connections])
  const sources = useNodesData(sourceIds)
  const source = sources[0]
  const sourceState = (source?.data.state ?? {}) as Record<string, unknown>
  const images = useMemo(() => workflowImages(sourceState), [sourceState])
  const image = images.find(i => i.id === state.outputId)
  const frame = studio.project?.artboards.find(f => f.id === state.frameId)
  const [error, setError] = useState<string | null>(null)
  const [working, setWorking] = useState(false); const guard = useRef(false)
  const canApply = !!frame && !!image && !!source && state.expectedVersionId !== undefined && !studio.busy && !working
  const parentDrifted = !!frame && state.expectedVersionId !== frame.versions[frame.activeVersion]?.id && !state.appliedVersionId
  async function apply() {
    if (!canApply || guard.current || !frame || !image || !source) return
    guard.current = true; setWorking(true); setError(null)
    const graphId = typeof state.graphId === 'string' ? state.graphId : 'workflow'
    const versionId = typeof state.resultVersionId === 'string' ? state.resultVersionId : crypto.randomUUID()
    update({ resultVersionId: versionId })
    try {
      const nodes = flow.getNodes(); const edges = flow.getEdges()
      // React Flow callbacks and measurements are renderer state; all JSON node data is retained.
      const page = JSON.parse(JSON.stringify({ nodes, edges })) as StudioWebPage
      await studio.writeOutput({ frameId: frame.id, expectedVersionId: typeof state.expectedVersionId === 'string' ? state.expectedVersionId : null,
        version: { id: versionId, label: image.label, params: {}, parentVersionId: typeof state.expectedVersionId === 'string' ? state.expectedVersionId : null,
          provenance: { graphId, nodeId: source.id, ...(image.jobId ? { jobId: image.jobId } : {}), ...(image.inputVersions ? { inputVersions: image.inputVersions } : {}), ...(image.settings ? { settings: image.settings } : {}), ...(image.model ? { model: image.model } : {}) } },
      }, image.url, page, graphId)
      update({ appliedVersionId: versionId })
    } catch (e) { setError((e as Error).message) }
    finally { guard.current = false; setWorking(false) }
  }
  return <>
    <NodeHandles nodeId={id} handles={STANDARD_HANDLES.FrameOutput} />
    <FrameConnection id={id} title={frame ? `Frame Output · ${frame.title}` : 'Frame Output'} selected={selected} state={state} onStateChange={update}>
      {image ? (
        <div className="relative w-full">
          <img src={image.url} alt="Selected workflow result" className="aspect-square w-full rounded-[10px] bg-muted object-contain" />
          {state.appliedVersionId ? (
            <span role="status" className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1 rounded-md bg-green-600/90 px-2 py-1 text-[11px] font-medium text-white">
              <Icon name="check" className="h-3 w-3" />
              Version saved
            </span>
          ) : parentDrifted ? (
            <span role="status" title="Artboard has a newer version than this output branches from. Resolve it in Node controls." className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1 rounded-md bg-amber-500/90 px-2 py-1 text-[11px] font-medium text-amber-950">
              <Icon name="info" className="h-3 w-3" />
              Parent outdated
            </span>
          ) : null}
        </div>
      ) : (
        <div className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed border-border px-4 text-center">
          <Icon name="frame" className="h-6 w-6 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{images.length ? 'No result chosen' : 'No result connected'}</span>
          <span className="text-xs text-muted-foreground">{images.length ? 'Pick one below' : 'Connect a node with an image output'}</span>
        </div>
      )}

      <div className="nodrag flex flex-col gap-2 p-2">
        <Select
          value={typeof image?.id === 'string' ? image.id : ''}
          onValueChange={value => update({ outputId: value, resultVersionId: crypto.randomUUID(), appliedVersionId: null })}
        >
          <SelectTrigger aria-label="Result to use" className="h-8 w-full" disabled={!images.length}>
            <SelectValue placeholder={images.length ? 'Choose a result' : 'Connect a node with an image output'} />
          </SelectTrigger>
          <SelectContent>
            {images.map(i => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button
          type="button"
          size="sm"
          className="w-full"
          disabled={!canApply || !!state.appliedVersionId}
          onClick={() => void apply()}
        >
          {working ? 'Saving version…' : !frame ? 'Choose a frame in Node controls' : 'Save as new version'}
        </Button>

        {error && <p role="alert" className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </FrameConnection>
  </>
}
export const FrameOutputNode = React.memo(FrameOutputComponent, (a, b) => a.id === b.id && a.selected === b.selected && a.data.state === b.data.state)
